import React, { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { username } from "../../Functionalities/Config";
import { showMessage } from "../../Functionalities/Functions";
import confirmBox from "../../CoreComponents/confirmBox";
import { fmtDate, rupees } from "../Insights/format";
import PaymentsTable from "./PaymentsTable";
import { standingLine } from "./riderReportData";
import { PAYMENT_MODES, blankPayment, deletePayment, fetchPayments, recordPayment, todayInIndia } from "./riderPaymentsData";

const Field = ({ label, children }) => (
  <label className="rider-form__field">
    <span className="stl-field__label">{label}</span>
    {children}
  </label>
);

function PaymentForm({ riderId, onSaved, onCancel }) {
  const [values, setValues] = useState(blankPayment);
  const [saving, setSaving] = useState(false);
  const change = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = await recordPayment(riderId, values);
    setSaving(false);
    if (body.Status !== true) {
      showMessage(body.Error || "The payment was not saved", "danger", "Not saved", "top", "top-right");
      return;
    }
    showMessage(`${rupees(body.payment.amount)} recorded`, "success", "Saved", "top", "top-right");
    onSaved();
  };

  return (
    <form className="rider-form" onSubmit={submit}>
      <Field label="Amount (₹)">
        <input className="stl-input" type="number" min="1" step="0.01" required value={values.amount} onChange={change("amount")} />
      </Field>
      <Field label="Paid on">
        <input className="stl-input" type="date" required max={todayInIndia()} value={values.paid_on} onChange={change("paid_on")} />
      </Field>
      <Field label="Mode">
        <select className="stl-input" value={values.mode} onChange={change("mode")}>
          {PAYMENT_MODES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="To account">
        <input className="stl-input" placeholder="e.g. Foodoor HDFC" value={values.account} onChange={change("account")} />
      </Field>
      <Field label="Reference">
        <input className="stl-input" placeholder="UPI or bank reference" value={values.reference} onChange={change("reference")} />
      </Field>
      <Field label="Collected by">
        <input className="stl-input" placeholder={username || "You"} value={values.collected_by} onChange={change("collected_by")} />
      </Field>
      <Field label="Note">
        <input className="stl-input" value={values.notes} onChange={change("notes")} />
      </Field>
      <div className="rider-form__actions">
        <button type="button" className="stl-btn stl-btn--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="stl-btn stl-btn--primary" disabled={saving}>
          {saving ? "Saving…" : "Save payment"}
        </button>
      </div>
    </form>
  );
}

export default function RiderPayments({ riderId, range, onChanged }) {
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchPayments({ riderId, range }).then((result) => {
      if (!live) return;
      setData(result);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [riderId, range, version]);

  const changed = () => {
    setAdding(false);
    setVersion((v) => v + 1);
    onChanged();
  };

  const remove = (payment) =>
    confirmBox({
      title: "Delete this payment?",
      message: `${rupees(payment.amount)} paid on ${fmtDate(payment.paid_on)}. The rider holds that cash again.`,
      yesLabel: "Delete",
      noLabel: "Cancel",
      onYes: () => removeConfirmed(payment),
    });

  const removeConfirmed = async (payment) => {
    const body = await deletePayment(payment.id);
    if (body.Status !== true) {
      showMessage(body.Error || "The payment was not deleted", "danger", "Not deleted", "top", "top-right");
      return;
    }
    changed();
  };

  const { rows, summary } = data;

  return (
    <div className="stl-card stl-table-card">
      <div className="stl-table-bar">
        <div className="stl-table-bar__title">
          <h2 className="stl-title">Previous settlements</h2>
          <span className="stl-subtitle">
            {rows.length} payment{rows.length === 1 ? "" : "s"}
            {summary ? ` · ${standingLine(summary)}` : ""}
          </span>
        </div>
        {!adding && (
          <button type="button" className="stl-btn stl-btn--sm stl-btn--primary" onClick={() => setAdding(true)}>
            <PlusOutlined /> Record payment
          </button>
        )}
      </div>
      {adding && <PaymentForm riderId={riderId} onSaved={changed} onCancel={() => setAdding(false)} />}
      <PaymentsTable rows={rows} loading={loading} onDelete={remove} />
    </div>
  );
}
